import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    insertFlowerCase,
    getProducts,
    checkPaymentStatusNoAuth,
    updatePaymentStatus
} from '../services/api';
import './FlowersServicePage.css';
// 1. UPDATED: Imported 'Search' icon
import { ChevronUp, ChevronDown, Trash2, Plus, Minus, Flower2, Search } from 'lucide-react';

// --- Helper: Product Variant Keys ---
const VARIANT_KEYS = ['small', 'medium', 'large'];

// --- Sub-component: Invoice Modal (Unchanged) ---
const InvoiceIframeModal = ({ isOpen, onClose, invoiceNumber, caseId }) => {
    const [paymentStatus, setPaymentStatus] = useState('pending');
    const [docType, setDocType] = useState('invoice');
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // Environment variables
    const pdfBaseUrl = process.env.REACT_APP_ZAMPAYPDF_BASE_URL;
    const payBaseUrl = process.env.REACT_APP_ZAMPAY_BASE_URL;

    useEffect(() => {
        if (isOpen) {
            setPaymentStatus('pending');
            setDocType('invoice');
            setIsButtonDisabled(false);
        }
    }, [isOpen, invoiceNumber, caseId]);

    const iframeSrc = `${pdfBaseUrl}/pdf?documentType=${docType}&invoiceNumber=${invoiceNumber}#navpanes=0&view=FitH&scrollbar=0`;

    // Polling Logic
    useEffect(() => {
        let pollInterval;

        if (isOpen && invoiceNumber && paymentStatus !== 'complete') {
            const checkStatus = async () => {
                try {
                    const response = await checkPaymentStatusNoAuth(invoiceNumber);
                    const status = response.data?.status || response.data?.paymentStatus;

                    if (status === 'Paid') {
                        setPaymentStatus('complete');
                        setDocType('receipt');
                        clearInterval(pollInterval);

                        if (caseId && invoiceNumber) {
                            try {
                                await updatePaymentStatus(caseId, invoiceNumber);
                                console.log(`Payment status updated for Case ID: ${caseId}`);
                            } catch (updateError) {
                                console.error("Failed to update payment status backend:", updateError);
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error checking invoice status:", error);
                }
            };

            pollInterval = setInterval(checkStatus, 5000);
        } else if (paymentStatus === 'complete' && pollInterval) {
            clearInterval(pollInterval);
        }

        return () => {
            if (pollInterval) clearInterval(pollInterval);
        };
    }, [isOpen, invoiceNumber, paymentStatus, caseId]);

    if (!isOpen || !invoiceNumber) return null;

    const handlePayment = () => {
        if (paymentStatus === 'complete') {
            console.log("Payment already complete. Cannot proceed.");
            return;
        }

        setPaymentStatus('in_progress');
        setIsButtonDisabled(true);
        const paymentUrl = `${payBaseUrl}/pay-for-service/get-invoice/${invoiceNumber}`;
        window.open(paymentUrl, '_blank');
    };

    return (
        <div className="modal-overlay" onClick={onClose} style={{ zIndex: 3000 }}>
            <div
                className="modal-content invoice-modal-content"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="invoice-modal-header">
                    <h2 className="modal-title" style={{ margin: 0, fontSize: '1.5rem' }}>
                        {docType === 'invoice' ? 'Invoice #' : 'Receipt #'}
                        {invoiceNumber}
                        {paymentStatus === 'complete' && <span className="status-badge-paid">✅ Paid</span>}
                    </h2>
                    <button className="close-modal-btn" style={{ position: 'static' }} onClick={onClose}>×</button>
                </div>

                <div className="iframe-container">
                    <iframe
                        src={iframeSrc}
                        title={`${docType} ${invoiceNumber}`}
                        className="pdf-iframe"
                    />
                </div>

                <div className="modal-footer-invoice">
                    {paymentStatus !== 'complete' && (
                        <button
                            className="checkout-btn"
                            onClick={handlePayment}
                            disabled={isButtonDisabled || paymentStatus === 'in_progress'}
                            style={{
                                width: 'auto',
                                opacity: (isButtonDisabled || paymentStatus === 'in_progress') ? 0.7 : 1,
                                cursor: (isButtonDisabled || paymentStatus === 'in_progress') ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {paymentStatus === 'in_progress' ? '⏳ Payment in Progress...' : 'Pay Now'}
                        </button>
                    )}
                    {paymentStatus === 'complete' && (
                        <button
                            className="checkout-btn"
                            onClick={onClose}
                            style={{ backgroundColor: '#48bb78', width: 'auto' }}
                        >
                            Finish
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Main Component ---
const FlowersServicePage = () => {
    const serviceId = '3e05d6ef-473d-4c2a-9fd5-747c8beef595';
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cart, setCart] = useState({});

    // UI State
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [quantityInModal, setQuantityInModal] = useState(1);

    // --- NEW STATE: Search Query ---
    const [searchQuery, setSearchQuery] = useState('');

    // --- NEW STATE: Cart Drawer Open/Close ---
    const [isCartOpen, setIsCartOpen] = useState(false);

    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [showGuestForm, setShowGuestForm] = useState(false);
    const [guestDetails, setGuestDetails] = useState({
        name: '',
        nrc: '',
        phoneNumber: '',
        email: ''
    });

    const [invoiceModalState, setInvoiceModalState] = useState({
        isOpen: false,
        invoiceNumber: null,
        caseId: null
    });

    const navigate = useNavigate();

    // Fetch Products
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await getProducts();
                setProducts(response.data);
            } catch (err) {
                console.error("Error fetching products:", err);
                setError("Failed to load products. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    // Helper to get variants of a product
    const getProductVariants = (product) => {
        return VARIANT_KEYS
            .filter(key => product[key] !== undefined && product[key] !== null)
            .map(key => ({
                key: key,
                price: product[key],
                stock: product[`qty_${key}`] || 0
            }))
            .sort((a, b) => a.price - b.price);
    };

    // Calculate total items and price using useMemo for performance
    const { totalItems, totalPrice } = useMemo(() => {
        let items = 0;
        let price = 0;

        Object.entries(cart).forEach(([uniqueCartId, qty]) => {
            const [id, variantKey] = uniqueCartId.split('_');
            const product = products.find(p => p.id === parseInt(id));

            if (product) {
                const unitPrice = product[variantKey] || 0;
                items += qty;
                price += unitPrice * qty;
            }
        });

        return { totalItems: items, totalPrice: price };
    }, [cart, products]);

    // Update cart quantity based on ID and Variant
    const updateCartQuantity = (productID, variantKey, quantity) => {
        const uniqueCartId = `${productID}_${variantKey}`;
        const product = products.find(p => p.id === productID);
        const stockKey = `qty_${variantKey}`;
        const maxStock = product ? (product[stockKey] || 0) : 0;

        // Ensure quantity doesn't exceed stock
        const validQuantity = Math.min(Math.max(0, quantity), maxStock);

        setCart(prev => {
            const newCart = { ...prev };
            if (validQuantity <= 0) {
                delete newCart[uniqueCartId];
            } else {
                newCart[uniqueCartId] = validQuantity;
            }
            return newCart;
        });
    };

    // Handler for clicking a product card
    const handleProductClick = (product) => {
        setSelectedProduct(product);
        const variants = getProductVariants(product);

        // Logic to pre-select a size with stock > 0
        let initialVariant = null;

        // Find first variant that has stock
        const availableVariant = variants.find(v => v.stock > 0);

        if (availableVariant) {
            initialVariant = availableVariant.key;
        } else if (variants.length > 0) {
            // Fallback to first if all are out of stock
            initialVariant = variants[0].key;
        }

        setSelectedVariant(initialVariant);

        // Check if in cart to pre-fill modal
        if (initialVariant) {
            const existingQty = cart[`${product.id}_${initialVariant}`] || 0;
            setQuantityInModal(existingQty > 0 ? existingQty : 1);
        } else {
            setQuantityInModal(1);
        }
    };

    // Update quantity in modal when variant changes
    useEffect(() => {
        if (selectedProduct && selectedVariant) {
            const existingQty = cart[`${selectedProduct.id}_${selectedVariant}`] || 0;
            setQuantityInModal(existingQty > 0 ? existingQty : 1);
        }
    }, [selectedVariant, selectedProduct, cart]);

    // Handler for adding items from the modal to the cart
    const handleAddToCartFromModal = () => {
        if (!selectedProduct || !selectedVariant) return;
        updateCartQuantity(selectedProduct.id, selectedVariant, quantityInModal);
        setSelectedProduct(null); // Close the modal
    };

    const handleGuestInputChange = (e) => {
        const { name, value } = e.target;
        setGuestDetails(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCheckoutClick = () => {
        if (totalItems === 0) return;
        setShowGuestForm(true);
    };

    const handleGuestSubmit = async (e) => {
        e.preventDefault();
        setIsCheckingOut(true);

        try {
            // Included product name in the transformed data
            const transformedProducts = Object.entries(cart).map(([uniqueCartId, qty]) => {
                const [id, variantKey] = uniqueCartId.split('_');
                const productId = parseInt(id);

                const product = products.find(p => p.id === productId);
                const unitprice = product ? (product[variantKey] || 0) : 0;
                const total = unitprice * qty;

                return {
                    id: productId,
                    name: product ? product.name : 'Unknown Product', // Added Name Here
                    qty: qty,
                    variant: variantKey,
                    unitprice: unitprice,
                    total: total
                };
            });

            const calculatedTotalCost = transformedProducts.reduce((acc, curr) => acc + curr.total, 0);

            const finalCartData = {
                products: transformedProducts,
                totalcost: calculatedTotalCost
            };

            // 2. Create a case for the guest
            const caseResponse = await insertFlowerCase({
                serviceId: serviceId,
                ...guestDetails,
                cartData: finalCartData
            });

            const newCaseId = caseResponse.data?.id;

            if (!newCaseId) {
                throw new Error("Failed to create case ID");
            }

            const newInvoiceNumber = caseResponse.data.case.invoicenumber;

            if (!newInvoiceNumber) {
                throw new Error("Invoice not generated");
            }

            // 4. Close Guest Form and Open Invoice Modal
            setShowGuestForm(false);
            setInvoiceModalState({
                isOpen: true,
                invoiceNumber: newInvoiceNumber,
                caseId: newCaseId
            });

        } catch (error) {
            console.error("Checkout failed:", error);
            alert("Failed to process checkout. Please try again.");
        } finally {
            setIsCheckingOut(false);
        }
    };

    const closeInvoiceModal = () => {
        setInvoiceModalState(prev => ({ ...prev, isOpen: false }));
        setCart({});
        navigate('/');
    };

    const getBaseUrl = () => {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        return apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl;
    };

    const baseUrl = getBaseUrl();

    // 3. UPDATED: Filtering logic
    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) return <div className="flowers-service-container"><p>MIHUD Nursery</p></div>;
    if (error) return <div className="flowers-service-container"><p className="error-message">{error}</p></div>;

    // Determine the product's cheapest price for display
    const getDisplayPrice = (product) => {
        const variants = getProductVariants(product);
        if (variants.length === 0) {
            return `K${parseFloat(product.price || product.medium || 0).toFixed(2)}`;
        }
        const minPrice = variants[0].price;
        const maxPrice = variants[variants.length - 1].price;

        if (variants.length === 1) return `K${parseFloat(minPrice).toFixed(2)}`;
        return `K${parseFloat(minPrice).toFixed(2)} - K${parseFloat(maxPrice).toFixed(2)}`;
    };

    return (
        <div className="flowers-service-container">
            <div className="flowers-header">
                {/* Replaced emoji with Lucid Flower Icon */}
                <h1 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                    MIHUD Nursery <Flower2 size={36} color="#ed64a6" />
                </h1>
                <p>From our nursery we offer a diverse range of plants. Select the size you need, order them online, and collect them at our nursery. Pay easily using ZamPay.</p>
            </div>

            {/* 4. UPDATED: Search Input Area */}
            <div className="search-container" style={{ maxWidth: '600px', margin: '0 auto 2rem auto', position: 'relative' }}>
                <Search className="search-icon" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#718096' }} size={20} />
                <input
                    type="text"
                    placeholder="Search plants by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '0.75rem 1rem 0.75rem 3rem',
                        borderRadius: '50px',
                        border: '1px solid #cbd5e0',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#38A169'}
                    onBlur={(e) => e.target.style.borderColor = '#cbd5e0'}
                />
            </div>

            <div className="products-grid">
                {/* 5. UPDATED: Use filteredProducts instead of products */}
                {filteredProducts.length > 0 ? (
                    filteredProducts.map(product => {
                        return (
                            <div
                                key={product.id}
                                className="product-card"
                                onClick={() => handleProductClick(product)}
                            >
                                <div className="card-image-wrapper">
                                    <img src={
                                        (() => {
                                            let url = 'https://via.placeholder.com/300?text=No+Image';
                                            if (product.image_url) {
                                                url = product.image_url.startsWith('http')
                                                    ? product.image_url
                                                    : `${baseUrl}/${product.image_url.startsWith('/') ? product.image_url.slice(1) : product.image_url}`;
                                            }
                                            return url;
                                        })()
                                    } alt={product.name} onError={(e) => {
                                        const lower = e.target.src.toLowerCase();
                                        if (e.target.src !== lower) { e.target.src = lower; }
                                        else { e.target.src = 'https://via.placeholder.com/300?text=Image+Error'; }
                                    }} />
                                </div>
                                <div className="card-content">
                                    <h3 className="card-title">{product.name}</h3>
                                    <div className="card-price">{getDisplayPrice(product)}</div>

                                    <div className="card-actions">
                                        <button
                                            className="add-btn"
                                            style={{
                                                backgroundColor: '#38A169',
                                                width: '100%',
                                                transition: 'background-color 0.2s',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleProductClick(product);
                                            }}
                                        >
                                            <span className="btn-text">Add to Cart</span> <span className="btn-icon">🛒</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: '#718096' }}>
                        <p>No plants found matching "{searchQuery}"</p>
                    </div>
                )}
            </div>

            {/* --- NEW CART DRAWER & CHECKOUT BAR --- */}
            {totalItems > 0 && (
                <div className={`cart-drawer-container ${isCartOpen ? 'open' : ''}`}>

                    {/* The Sliding Drawer Content */}
                    <div className="cart-list-drawer">
                        <div className="drawer-header">
                            <h3>Your Cart ({totalItems})</h3>
                            <button className="close-drawer-btn" onClick={() => setIsCartOpen(false)}>
                                <ChevronDown size={24} />
                            </button>
                        </div>

                        <div className="cart-items-scroll">
                            {Object.entries(cart).map(([uniqueId, qty]) => {
                                const [id, variant] = uniqueId.split('_');
                                const product = products.find(p => p.id === parseInt(id));
                                if (!product) return null;

                                let imgUrl = 'https://via.placeholder.com/100';
                                if (product.image_url) {
                                    imgUrl = product.image_url.startsWith('http')
                                        ? product.image_url
                                        : `${baseUrl}/${product.image_url.startsWith('/') ? product.image_url.slice(1) : product.image_url}`;
                                }

                                return (
                                    <div key={uniqueId} className="cart-drawer-item">
                                        <img src={imgUrl} alt="thumb" className="drawer-item-img" onError={(e) => {
                                            const lower = e.target.src.toLowerCase();
                                            if (e.target.src !== lower) { e.target.src = lower; }
                                            else { e.target.src = 'https://via.placeholder.com/100'; }
                                        }} />
                                        <div className="drawer-item-info">
                                            <span className="drawer-item-name">{product.name}</span>
                                            <span className="drawer-item-variant">{variant.toUpperCase()}</span>
                                            <span className="drawer-item-price">K{(product[variant] * qty).toFixed(2)}</span>
                                        </div>
                                        <div className="drawer-item-controls">
                                            <button
                                                className="drawer-ctrl-btn"
                                                onClick={() => updateCartQuantity(product.id, variant, qty - 1)}
                                            >
                                                <Minus size={16} />
                                            </button>
                                            <span className="drawer-qty">{qty}</span>
                                            <button
                                                className="drawer-ctrl-btn"
                                                onClick={() => updateCartQuantity(product.id, variant, qty + 1)}
                                            >
                                                <Plus size={16} />
                                            </button>
                                            <button
                                                className="drawer-delete-btn"
                                                onClick={() => updateCartQuantity(product.id, variant, 0)}
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* The Floating Bar */}
                    <div className="checkout-bar">
                        {/* Edit Cart Button */}
                        <button
                            className="toggle-cart-btn"
                            onClick={() => setIsCartOpen(!isCartOpen)}
                        >
                            {isCartOpen ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                            {isCartOpen ? "Hide Cart" : "Edit Cart"}
                        </button>

                        <div className="cart-total-info">
                            <span className="total-label">Total:</span>
                            <span className="total-amount">K{totalPrice.toFixed(2)}</span>
                        </div>

                        <button
                            className="checkout-btn-final"
                            onClick={() => setShowGuestForm(true)}
                        >
                            Checkout
                        </button>
                    </div>
                </div>
            )}

            {/* --- Product Detail/Variant Selection Modal --- */}
            {selectedProduct && (
                <div className="modal-overlay" onClick={() => setSelectedProduct(null)}>
                    <div className="modal-content product-detail-modal" onClick={e => e.stopPropagation()}>

                        <div className="modal-image">
                            <img src={
                                (() => {
                                    let url = 'https://via.placeholder.com/300?text=No+Image';
                                    if (selectedProduct.image_url) {
                                        url = selectedProduct.image_url.startsWith('http')
                                            ? selectedProduct.image_url
                                            : `${baseUrl}/${selectedProduct.image_url.startsWith('/') ? selectedProduct.image_url.slice(1) : selectedProduct.image_url}`;
                                    }
                                    return url;
                                })()
                            } alt={selectedProduct.name} onError={(e) => {
                                const lower = e.target.src.toLowerCase();
                                if (e.target.src !== lower) { e.target.src = lower; }
                                else { e.target.src = 'https://via.placeholder.com/300?text=Image+Error'; }
                            }} />
                        </div>

                        <div className="modal-details">
                            <button className="close-modal-btn" onClick={() => setSelectedProduct(null)}>×</button>
                            <h2 className="modal-title">{selectedProduct.name}</h2>
                            <p className="modal-description">{selectedProduct.description}</p>

                            <hr style={{ margin: '1rem 0' }} />

                            <div className="variant-selection-area" style={{ marginBottom: '1.5rem' }}>
                                <span style={{ fontWeight: '600', display: 'block', marginBottom: '0.5rem' }}>Choose Size:</span>
                                <div className="size-buttons" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    {getProductVariants(selectedProduct).map(variant => {
                                        const isSelected = selectedVariant === variant.key;
                                        const isOutOfStock = variant.stock === 0;
                                        const baseColor = '#38A169';
                                        const borderColor = isSelected ? baseColor : '#cbd5e0';
                                        const backgroundColor = isSelected ? '#f0fff4' : '#ffffff';

                                        return (
                                            <button
                                                key={variant.key}
                                                onClick={() => {
                                                    setSelectedVariant(variant.key);
                                                }}
                                                disabled={isOutOfStock}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    border: `2px solid ${borderColor}`,
                                                    borderRadius: '8px',
                                                    backgroundColor: backgroundColor,
                                                    color: isOutOfStock ? '#a0aec0' : '#2d3748',
                                                    cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                                                    opacity: isOutOfStock ? 0.6 : 1,
                                                    fontWeight: '500',
                                                    boxShadow: isSelected ? `0 0 0 1px ${baseColor} inset` : 'none',
                                                }}
                                            >
                                                {variant.key.charAt(0).toUpperCase() + variant.key.slice(1)} (K{parseFloat(variant.price).toFixed(2)})
                                                {isOutOfStock && <span> (Out of Stock)</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {selectedVariant && (
                                <>
                                    <div className="price-info" style={{ marginBottom: '1rem' }}>
                                        <span style={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                                            Price: K{parseFloat(selectedProduct[selectedVariant]).toFixed(2)}
                                        </span>
                                        <span className="stock-badge" style={{ marginLeft: '1rem', color: selectedProduct[`qty_${selectedVariant}`] > 0 ? '#38a169' : '#e53e3e', fontWeight: 'bold' }}>
                                            {selectedProduct[`qty_${selectedVariant}`] || 0} available
                                        </span>
                                    </div>

                                    <div className="quantity-row" style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                                        <span style={{ fontWeight: '600' }}>Quantity:</span>
                                        <div className="quantity-controls">
                                            <button
                                                className="qty-btn"
                                                onClick={() => setQuantityInModal(prev => Math.max(0, prev - 1))}
                                            >
                                                −
                                            </button>
                                            <span className="qty-value">{quantityInModal}</span>
                                            <button
                                                className="qty-btn"
                                                onClick={() => setQuantityInModal(prev => Math.min((selectedProduct[`qty_${selectedVariant}`] || 1), prev + 1))}
                                                disabled={quantityInModal >= (selectedProduct[`qty_${selectedVariant}`] || 0)}
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    <button
                                        className="checkout-btn"
                                        onClick={handleAddToCartFromModal}
                                        disabled={selectedProduct[`qty_${selectedVariant}`] === 0}
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        {quantityInModal === 0 ? 'Remove from Cart' :
                                            (cart[`${selectedProduct.id}_${selectedVariant}`] ? 'Update Cart' : 'Add to Cart')}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Guest Checkout Modal */}
            {showGuestForm && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '500px', flexDirection: 'column', padding: '2rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h2 style={{ margin: 0 }}>Guest Checkout</h2>
                            <button className="close-modal-btn" style={{ position: 'static' }} onClick={() => setShowGuestForm(false)}>×</button>
                        </div>

                        <form onSubmit={handleGuestSubmit}>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>NRC Number *</label>
                                <input type="text" name="nrc" required value={guestDetails.nrc} onChange={handleGuestInputChange} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e0' }} placeholder="Enter your NRC" />
                            </div>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Name *</label>
                                <input type="text" name="name" required value={guestDetails.name} onChange={handleGuestInputChange} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e0' }} placeholder="Enter your name" />
                            </div>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Phone Number *</label>
                                <input type="tel" name="phoneNumber" required value={guestDetails.phoneNumber} onChange={handleGuestInputChange} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e0' }} placeholder="Enter your phone number" />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Email Address (Optional)</label>
                                <input type="email" name="email" value={guestDetails.email} onChange={handleGuestInputChange} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e0' }} placeholder="Enter your email" />
                            </div>

                            <button type="submit" className="checkout-btn" style={{ width: '100%', justifyContent: 'center' }} disabled={isCheckingOut}>
                                {isCheckingOut ? 'Processing...' : `Pay K${totalPrice.toFixed(2)}`}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Invoice Modal */}
            <InvoiceIframeModal
                isOpen={invoiceModalState.isOpen}
                onClose={closeInvoiceModal}
                invoiceNumber={invoiceModalState.invoiceNumber}
                caseId={invoiceModalState.caseId}
            />
        </div>
    );
};

export default FlowersServicePage;